import React, { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import './Login.css';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { API_URL } from '../global';
import Swal from 'sweetalert2';

const LOGO  = "https://res.cloudinary.com/dwrwkl5b3/image/upload/v1772788624/ChatGPT_Image_Mar_6__2026__02_43_41_PM-removebg-preview_wyghgu.png";
const DRONE = "https://res.cloudinary.com/dwrwkl5b3/image/upload/v1772684468/front_face_drone_AJEET_TRAINER_dcqaim.png";

/* ── Inline SVG Icons (no FontAwesome needed) ── */
const IconEmail = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ff6a00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <polyline points="2,4 12,13 22,4"/>
  </svg>
);

const IconLock = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ff6a00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const IconEye = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ff6a00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconEyeOff = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ff6a00" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const IconLogin = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
    <polyline points="10 17 15 12 10 7"/>
    <line x1="15" y1="12" x2="3" y2="12"/>
  </svg>
);

const Login = () => {
  const [showPass, setShowPass] = useState(false);
  const [headerH,  setHeaderH]  = useState(75);
  const navigate                = useNavigate();
  const { saveUser }            = useContext(UserContext);

  /* detect actual navbar height */
  useEffect(() => {
    const measure = () => {
      const nav = document.querySelector('nav') || document.querySelector('header');
      if (nav) setHeaderH(nav.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object({
      email:    Yup.string().email('Invalid email address').required('Email is required'),
      password: Yup.string().required('Password is required'),
    }),
    onSubmit: async (values) => {
      Swal.fire({
        title: 'Logging in…',
        text: 'Please wait',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      try {
        const res = await axios.post(`${API_URL}/login`, values);
        if (res.data.message === 'Successfully Logged In') {
          saveUser({
            role:     res.data.role,
            user_id:  res.data.auth === 'faculty' ? res.data.college_id : res.data._id,
            batch_id: res.data.batch_id,
            auth:     res.data.auth,
          });
          Swal.fire({
            icon: 'success', title: 'Login Successful',
            text: res.data.message, timer: 2000, showConfirmButton: false,
          }).then(() => navigate('/lms/homepage'));
        } else {
          Swal.fire({ icon: 'error', title: 'Login Failed', text: res.data.message });
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Something went wrong!' });
      }
    },
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div
      className="zuppa_lms_login_pg_root"
      style={{ marginTop: `${headerH}px`, minHeight: `calc(100vh - ${headerH}px)` }}
    >

      {/* ══ LEFT PANEL ══ */}
      <div className="zuppa_lms_login_pg_panel">
        <span className="zuppa_lms_login_pg_bubble zuppa_lms_login_pg_bubble_1" />
        <span className="zuppa_lms_login_pg_bubble zuppa_lms_login_pg_bubble_2" />
        <span className="zuppa_lms_login_pg_bubble zuppa_lms_login_pg_bubble_3" />

        <div className="zuppa_lms_login_pg_panel_body">
          <div className="zuppa_lms_login_pg_drone_wrap">
            <img src={DRONE} alt="Zuppa Drone" className="zuppa_lms_login_pg_drone_img" />
            <div className="zuppa_lms_login_pg_drone_shadow" />
          </div>
          <h2 className="zuppa_lms_login_pg_panel_title">Zuppa LMS</h2>
          <p className="zuppa_lms_login_pg_panel_sub">
            Empowering drone education.<br />Sign in to take flight.
          </p>
          <div className="zuppa_lms_login_pg_dots">
            <span /><span /><span />
          </div>
        </div>
      </div>

      {/* ══ RIGHT — LOGIN FORM ══ */}
      <div className="zuppa_lms_login_pg_form_side">
        <form
          className="zuppa_lms_login_pg_form"
          onSubmit={formik.handleSubmit}
          noValidate
        >
          <img src={LOGO} alt="Zuppa Logo" className="zuppa_lms_login_pg_logo" />
          <h2 className="zuppa_lms_login_pg_form_title">
            Welcome <span>Back</span>
          </h2>
          <p className="zuppa_lms_login_pg_form_sub">Sign in to your account</p>

          {/* ── Email Field ── */}
          <div className="zuppa_lms_login_pg_field">
            <div className="zuppa_lms_login_pg_input_wrap">
              <span className="zuppa_lms_login_pg_field_icon">
                <IconEmail />
              </span>
              <input
                type="email"
                placeholder="Email Address"
                className={`zuppa_lms_login_pg_input${
                  formik.touched.email && formik.errors.email
                    ? ' zuppa_lms_login_pg_input_bad' : ''
                }`}
                {...formik.getFieldProps('email')}
              />
            </div>
            {formik.touched.email && formik.errors.email && (
              <p className="zuppa_lms_login_pg_err">{formik.errors.email}</p>
            )}
          </div>

          {/* ── Password Field ── */}
          <div className="zuppa_lms_login_pg_field">
            <div className="zuppa_lms_login_pg_input_wrap">
              <span className="zuppa_lms_login_pg_field_icon">
                <IconLock />
              </span>
              <input
                type={showPass ? 'text' : 'password'}
                placeholder="Password"
                className={`zuppa_lms_login_pg_input${
                  formik.touched.password && formik.errors.password
                    ? ' zuppa_lms_login_pg_input_bad' : ''
                }`}
                {...formik.getFieldProps('password')}
              />
              <button
                type="button"
                className="zuppa_lms_login_pg_eye"
                onClick={() => setShowPass(p => !p)}
                tabIndex={-1}
                aria-label="Toggle password visibility"
              >
                {showPass ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <p className="zuppa_lms_login_pg_err">{formik.errors.password}</p>
            )}
          </div>

          {/* ── Forgot Password ── */}
          <span
            className="zuppa_lms_login_pg_forgot"
            onClick={() => navigate('/lms/forgot-password')}
          >
            Forgot Password?
          </span>

          {/* ── Submit ── */}
          <button type="submit" className="zuppa_lms_login_pg_btn">
            <IconLogin /> Login
          </button>
        </form>
      </div>

    </div>
  );
};

export default Login;